<template>
    <div>
        <v-form v-if="ready" ref="form" @submit.prevent v-model="isValid">
            <fe-label class="mt-0">Visualization Name</fe-label>
            <v-text-field
                v-model="config.name"
                counter=50
                :rules="this.$fieldValidators('text', null, {required: true, limit: 50})"
                flat solo dense clearable autofocus
            />

            <group-by v-model="config.chart_types" :visType="config.config.type"/>

            <fe-remote-combo
                label="Report As"
                :items="reportAsOptions"
                v-model="config.report_as"
                :rules="this.$fieldValidators('select', null, {required: true})"
                byId
            />

            <fe-remote-combo
                v-if="config.report_as === 'targets'"
                label="Show As"
                :items="showAsOptions"
                v-model="config.value"
                :rules="this.$fieldValidators('select', null, {required: true})"
                byId
            />

            <fe-remote-combo
                v-if="config.report_as === 'targets'"
                label="Reporting Rate"
                :items="rateOptions"
                v-model="config.target_type_id"
                :rules="this.$fieldValidators('select', null, {required: true})"
                byId
            />
            <vis-type v-model="config.visualization"/>

            <student-filters
                v-if="!globalFilters"
                :filters="studentFilters"
                :dashboard="dashboard"
                minDays
                @change="studentFilters = $event"
            />

            <fe-label>Enter a Short Description</fe-label>
            <v-textarea
                v-model="config.description"
                counter=255
                rows=4
                :rules="this.$fieldValidators('text', null, {required: false, limit: 255})"
                flat solo dense no-resize
            />

            <display-settings :value="displaySettings" @change="displaySettings = $event"/>
        </v-form>
    </div>
</template>

<script>
import VisType from "./fields/VisType"
import GroupBy from "./fields/GroupBy"
import StudentFilters from "./fields/StudentFilters"
import DisplaySettings from "./fields/DisplaySettings"

export default {
    name: "Attendance",

    components: {
        VisType,
        GroupBy,
        StudentFilters,
        DisplaySettings
    },

    props: {
        dashboard: {
            type: Object,
            required: true
        },
        edit: {
            type: Object,
            required: false
        },
        getSSValues: {
            type: Function,
            required: true
        },
        setConfig: {
            type: Function,
            required: true
        },
        globalFilters: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            ready: false,
            isValid: false,
            config: {
                name: '',
                chart_types: undefined,
                visualization: undefined,
                report_as: undefined,
                target_type_id: undefined,
                colors: [],
                yAxisMin: undefined,
                yAxisMax: undefined,
                description: '',
                config: {
                    type: 'attendance',
                    chart_types: undefined,
                    visualization: undefined,
                    report_as: undefined,
                    target_type_id: undefined,
                    colors: undefined,
                    yAxisMin: undefined,
                    yAxisMax: undefined,
                    description: undefined
                }
            },
            savedSearch: {
                school_id: undefined,
                grade_id: undefined,
                ethnicity: undefined,
                disability_type_id: undefined,
                gender: undefined,
                tag_id: undefined,
                meal_status_id: undefined,
                ell_flag: undefined,
                ell_level_id: undefined,
                student_active_flag: undefined,
                student_active_date: undefined,
                min_possible_attendance_days: undefined,
                demographic: undefined
            },
            reportAsOptions: [{
                id: 'rate',
                name: 'Overall Rate'
            }, {
                id: 'targets',
                name: 'Performance Bands'
            }],
            showAsOptions: [{
                id: 'student_total',
                name: 'Total Students'
            }, {
                id: 'percentage',
                name: 'Percent of Students'
            }],
            rateOptions: [{
                id: 2,
                name: 'Full Day Rate',
                longName: 'Full Day Attendance Rate'
            }, {
                id: 1,
                name: 'SIS Reported Rate',
                longName: 'SIS Reported Attendance Rate'
            }],
        }
    },

    computed: {
        displaySettings: {
            get() {
                return {
                    colors: this.config.colors,
                    yAxisMin: this.config.yAxisMin,
                    yAxisMax: this.config.yAxisMax
                }
            },

            set(v) {
                Object.keys(v).forEach(x => {
                    if (this.config.hasOwnProperty(x)) {
                        if (x === 'color') {
                            this.log(v[x])
                            this.config[x] = v[x]
                        } else {
                            this.config[x] = v[x]
                        }
                    }
                })
            }
        },

        studentFilters: {
            get() {
                return {
                    school_id: this.savedSearch.school_id,
                    grade_id: this.savedSearch.grade_id,
                    ethnicity: this.savedSearch.ethnicity,
                    disability_type_id: this.savedSearch.disability_type_id,
                    gender: this.savedSearch.gender,
                    tag_id: this.savedSearch.tag_id,
                    min_possible_attendance_days: this.savedSearch.min_possible_attendance_days?.id,
                    meal_status_id: this.savedSearch.meal_status_id,
                    ell_flag: this.savedSearch.ell_flag,
                    ell_level_id: this.savedSearch.ell_level_id,
                    student_active_flag: this.savedSearch.student_active_flag,
                    student_active_date: this.savedSearch.student_active_date,
                    demographic: this.savedSearch.demographic
                }
            },

            set(v) {
                Object.keys(v).forEach(x => {
                    if (this.savedSearch.hasOwnProperty(x)) {
                        this.savedSearch[x] = v[x]
                    }
                })
            }
        }
    },

    watch: {
        isValid(n, o) {
            if (n !== o) {
                this.$emit('valid', n)
            }
        },

        config: {
            deep: true,
            handler(v) {
                if (v.report_as === 'rate') v.value = "student_total"
                this.$emit('change', v, 'config')
            }
        },

        savedSearch: {
            deep: true,
            handler(v) {
                this.$emit('change', v, 'savedSearch')
            }
        }
    },

    mounted() {
        let awaiting = []

        if (this.edit) {
            this.config = this.setConfig(this.config, this.edit)
            awaiting.push(this.getSSValues(this.config.saved_search_id, this.savedSearch))
        }

        Promise.all(awaiting)
            .then(() => {
                this.setColors()
                if (this.globalFilters) {
                    this.getSSValues(this.dashboard.saved_search_id, this.savedSearch)
                        .then(() => {
                            this.ready = true
                        })
                } else {
                    this.ready = true
                }
            })
    },

    methods: {
        setColors() {
            let colors = this.config.colors
            this.config.colors = this.rateOptions.map(x => {
                let color = colors[x.longName] || null
                return {
                    name: x.longName,
                    id: x.longName,
                    color: color
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>

</style>
